/**
 * 红眼工具（补齐 registry.ts 早先登记的占位工具）。
 *
 * 只处理"确实偏红"的像素，不是把点击处一整个圆都涂黑——闪光灯红眼的
 * 典型特征是瞳孔反光的红通道明显高于绿/蓝，用这条简单判据筛出需要
 * 处理的像素，其余像素在圆圈范围内原样不动。修复方式：用绿蓝均值当
 * 去红后的基准灰度，再压暗一截（瞳孔本该是暗色，不只是去掉红色偏移）。
 */
import { compositeDocument } from './composite';
import { applyFill } from '../engine/fill/apply';
import type { AppStore } from './store';

/**
 * 以文档坐标 (x, y) 为圆心、radius 为半径，修复范围内偏红的像素。
 * 一次调用 = 一条历史。返回是否有修改发生。
 */
export function redEyeFix(store: AppStore, x: number, y: number, radius: number): boolean {
  const document = store.document;
  if (document === null) return false;
  const target = store.activeRasterLayerId();
  if (target === null) return false;

  const cx = Math.floor(x);
  const cy = Math.floor(y);
  const r = Math.max(1, Math.round(radius));
  const bx0 = Math.max(0, cx - r);
  const by0 = Math.max(0, cy - r);
  const bx1 = Math.min(document.size.width, cx + r);
  const by1 = Math.min(document.size.height, cy + r);
  if (bx1 <= bx0 || by1 <= by0) return false;

  const docWidth = document.size.width;
  const composite = compositeDocument(document, store.tiles);

  const transaction = store.beginPixelTransaction('红眼');
  const touched = applyFill(
    store.tiles,
    target,
    { x: bx0, y: by0, width: bx1 - bx0, height: by1 - by0 },
    (px, py) => {
      const dx = px + 0.5 - x;
      const dy = py + 0.5 - y;
      if (dx * dx + dy * dy > r * r) return [0, 0, 0, 0]; // 圆外不动
      const at = (py * docWidth + px) * 4;
      const rr = composite.pixels[at]!;
      const gg = composite.pixels[at + 1]!;
      const bb = composite.pixels[at + 2]!;
      const aa = composite.pixels[at + 3]!;
      // 红眼判据：红通道明显高于绿/蓝，不够红的像素原样跳过
      if (rr < gg * 1.15 || rr < bb * 1.15) return [0, 0, 0, 0];
      const gray = Math.round((gg + bb) / 2); // 去掉红色偏移，用绿蓝均值当基准灰
      const darkened = Math.round(gray * 0.5); // 瞳孔本该是暗色，顺带压暗
      return [darkened, darkened, darkened, aa];
    },
    transaction,
  );
  if (touched.length === 0) return false;
  store.commitPixels('红眼', transaction.end());
  store.invalidatePixels(touched);
  return true;
}
